import { Injectable } from '@angular/core';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import {
  DetailsState,
  GetInventoryItemDetails,
  GetInventoryItemBasic,
  GetInventoryItemDistance,
  GetInventoryItemQuestions,
  AmbassadorState,
  GetVehicleOffers,
  AnswerQuestion,
  GetDealerQuestions,
  DecrementOpenOffers,
  GetAuctionItem,
  SetAuctionItemSold
} from 'remarketing-angular-library';
import { catchError, delay, mapTo, mergeMap, switchMap, tap } from 'rxjs/operators';
import { BehaviorSubject, defer, iif, merge, of, Subject } from 'rxjs';

@Injectable()
export class InventoryItemDataService {
  private lastRemoteUpdateTime = 0;
  private readonly timeBeforeRefresh = 30 * 1000; // 30 seconds
  private readonly completeDetails$$ = new Subject();
  private readonly basicDetails$$ = new Subject();

  public _localErrorState: BehaviorSubject<{ message: string, offerUuid: string }> = new BehaviorSubject({ message: null, offerUuid: null });
  public localErrorState = this._localErrorState.asObservable();

  public _globalErrorState: BehaviorSubject<{ message: string, showErrorBanner: boolean }> = new BehaviorSubject({ message: null, showErrorBanner: false });
  public globalerrorState = this._globalErrorState.asObservable();

  public _globalOfferError: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public globalOfferError = this._globalOfferError.asObservable();

  public _globalQuestionError: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public globalQuestionError = this._globalQuestionError.asObservable();


  constructor(private readonly store: Store, private readonly actions$: Actions) { }

  listen$(inventoryItemUuid: string) {
    const complete$ = this.completeDetails$$.pipe(mapTo('complete'));
    const basic$ = this.basicDetails$$.pipe(mapTo('basic'));

    /*
    We listen for anything that could change the vehicle's status and pause updating it from the store.
     */
    const updateActions$ = this.actions$.pipe(
      ofActionDispatched(AnswerQuestion, SetAuctionItemSold, DecrementOpenOffers),
      tap(() => this.setUpdated())
    );

    /*
    Take all requests to update a vehicle's data and ensure that the correct one is dispatched and the data is updated.
     */
    const dataActions$ = merge(complete$, basic$).pipe(
      switchMap((type: string) => {
        const dealerUuid: string = this.store.selectSnapshot(AmbassadorState.selectedAmbassadorDealershipUuid);
        return of({}).pipe(
          this.delayOperator(),
          mergeMap(() =>
            iif(
              () => type === 'complete',
              defer(() => this.store.dispatch(new GetInventoryItemDetails(inventoryItemUuid, dealerUuid))),
              defer(() => this.store.dispatch(new GetInventoryItemBasic(inventoryItemUuid, dealerUuid)))
            )
          )
        );
      }),
      tap(() => {
        this.fetchAdditionalData();
      }),
      catchError(() => {
        return of({});
      })
    );

    return merge(dataActions$, updateActions$);
  }

  fetchCompleteDetails() {
    this.completeDetails$$.next(true);
  }

  fetchBasicDetails() {
    this.basicDetails$$.next(true);
  }

  setUpdated() {
    this.lastRemoteUpdateTime = Date.now();
  }

  private fetchAdditionalData() {
    const organization = this.store.selectSnapshot(state => state.org.organization);
    const inventoryItem = this.store.selectSnapshot(DetailsState.inventoryItem);
    this.store.dispatch(new GetAuctionItem(inventoryItem.auctionItemUuid));
    this.store.dispatch(new GetDealerQuestions(inventoryItem.inventoryItemUuid));
    this.store.dispatch(new GetInventoryItemDistance(inventoryItem.inventoryItemUuid, organization));
    this.store.dispatch(new GetInventoryItemQuestions(inventoryItem.inventoryItemUuid));
    this.store.dispatch(new GetVehicleOffers(inventoryItem.auctionItemUuid));
  }

  /**
   * We want to delay at least 1 minute (or whatever is configured) before updating with remote data to allow
   * the sync to happen.
   */
  private delayOperator() {
    const timeSinceUpdate = Date.now() - this.lastRemoteUpdateTime;
    const delayAmount = timeSinceUpdate > this.timeBeforeRefresh ? 0 : this.timeBeforeRefresh - timeSinceUpdate;
    return delay(delayAmount);
  }
}
